import Matter from "matter-js";
import * as C from "../../config/constants.js";
import { clamp, getBodyHalfHeight, getFlaskBodyTopY, getFlaskBodyVerticalExtents, getOtherBodyInPair } from "./math.js";
import {
  getErlenmeyerHalfWidthAtY,
  getFlaskMouthDampSlackPx,
  getFlaskMouthMaxUpwardSpeed,
  getFlaskMouthOvershootMinPx,
  getPhysicsMouthGateTopY,
  scaleFlaskPhysicsPx,
} from "./geometry.js";

function flaskBodySpeed(body) {
  if (!body || !body.velocity) return 0;
  var vx = body.velocity.x || 0;
  var vy = body.velocity.y || 0;
  return Math.sqrt(vx * vx + vy * vy);
}

function isFlaskCargo(meta) {
  return !!(meta && meta._flaskEntered);
}

function getPhysicsHeadroomPx(geom) {
  if (!geom || geom.T == null) return 0;
  var physicsTopY = getPhysicsMouthGateTopY(geom);
  if (!(physicsTopY < geom.T)) return 0;
  return geom.T - physicsTopY;
}

function getExtendedHeadroomPx(system) {
  var geom = system && system._flaskGeometry;
  return getPhysicsHeadroomPx(geom);
}

function getMouthMaxUpwardSpeed(system) {
  var base = (system && system.bounds)
    ? getFlaskMouthMaxUpwardSpeed(system.bounds)
    : ((C.FLASK_MOUTH_MAX_UPWARD_SPEED != null) ? Number(C.FLASK_MOUTH_MAX_UPWARD_SPEED) : 6);
  if (!isFinite(base) || base <= 0) base = 6;
  var headroom = getExtendedHeadroomPx(system);
  if (headroom > 1) return Math.max(base, headroom * 0.5);
  return base;
}

function isInExtendedHeadroom(bodyTop, geom) {
  if (!geom) return false;
  var physicsTopY = getPhysicsMouthGateTopY(geom);
  if (!(physicsTopY < geom.T)) return false;
  return bodyTop <= geom.T + getFlaskMouthDampSlackPx({ top: geom.T, bottom: geom.bodyBottom })
    && bodyTop > physicsTopY;
}

function getMouthDampSlackPx(system) {
  if (system && system.bounds) return getFlaskMouthDampSlackPx(system.bounds);
  var slack = (C.FLASK_MOUTH_DAMP_SLACK_PX != null)
    ? Number(C.FLASK_MOUTH_DAMP_SLACK_PX)
    : 2;
  return isFinite(slack) && slack >= 0 ? slack : 2;
}

function getMouthSweepFactor() {
  var factor = (C.FLASK_MOUTH_SWEEP_FACTOR != null)
    ? Number(C.FLASK_MOUTH_SWEEP_FACTOR)
    : 1.1;
  return isFinite(factor) && factor > 0 ? factor : 1.1;
}

function getMouthOvershootPx(halfH, bounds) {
  var minPx = bounds
    ? getFlaskMouthOvershootMinPx(bounds)
    : ((C.FLASK_MOUTH_OVERSHOOT_PX != null) ? Number(C.FLASK_MOUTH_OVERSHOOT_PX) : 8);
  var halfMult = (C.FLASK_MOUTH_OVERSHOOT_HALF_MULT != null)
    ? Number(C.FLASK_MOUTH_OVERSHOOT_HALF_MULT)
    : 0.5;
  if (!isFinite(minPx) || minPx < 0) minPx = 8;
  if (!isFinite(halfMult) || halfMult < 0) halfMult = 0.5;
  return Math.max(minPx, halfH * halfMult);
}

/** Visual rim (bounds.top) — mouth damp/sweep band is anchored here. */
export function getMouthEnforceTopY(clampTopY, halfH, bounds) {
  return clampTopY - getMouthOvershootPx(halfH, bounds);
}

/** True when the body top is at the flask lip (not deep in the neck). */
function isAtFlaskRimEdge(bodyTop, clampTopY, system) {
  return bodyTop <= clampTopY + getMouthDampSlackPx(system);
}

function getFlaskWallContainmentBounce() {
  var bounce = (C.FLASK_WALL_CONTAINMENT_BOUNCE != null)
    ? Number(C.FLASK_WALL_CONTAINMENT_BOUNCE)
    : 0.52;
  return isFinite(bounce) && bounce > 0 ? bounce : 0.52;
}

function getFlaskWallGlanceFactor() {
  var glance = (C.FLASK_WALL_GLANCE_FACTOR != null)
    ? Number(C.FLASK_WALL_GLANCE_FACTOR)
    : 0.18;
  return isFinite(glance) && glance >= 0 ? glance : 0.18;
}

export function applyFlaskWallSlideGlance(meta, cargoBody, wallBody, collision) {
  if (!meta || !cargoBody || !wallBody || !collision || cargoBody.isStatic) return;
  if (!meta._flaskEntered) return;

  var now = Date.now();
  if (meta._wallGlanceAt && now - meta._wallGlanceAt < 48) return;

  var nx = collision.normal.x;
  var ny = collision.normal.y;
  if (cargoBody.id === collision.bodyB.id) {
    nx = -nx;
    ny = -ny;
  }
  if (Math.abs(nx) < 0.55) return;

  var vx = cargoBody.velocity ? cargoBody.velocity.x : 0;
  var vy = cargoBody.velocity ? cargoBody.velocity.y : 0;
  var dot = vx * nx + vy * ny;
  var tx = -ny;
  var ty = nx;
  var tang = vx * tx + vy * ty;

  if (Math.abs(tang) < 1.2 || Math.abs(tang) < Math.abs(dot) * 1.35) return;

  var glance = getFlaskWallGlanceFactor();
  var kick = Math.min(2.8, Math.abs(tang) * glance);
  Matter.Body.setVelocity(cargoBody, {
    x: vx + nx * kick,
    y: vy * (1 - glance * 0.25),
  });
  meta._wallGlanceAt = now;
}

function applyWallContainmentBounce(vx, vy, hitRight, hitLeft, hitFloor) {
  var bounce = getFlaskWallContainmentBounce();
  var glance = getFlaskWallGlanceFactor();
  var nextVx = vx;
  var nextVy = vy;

  if (hitRight || hitLeft) {
    var outward = hitRight ? -1 : 1;
    var intoWall = hitRight ? Math.max(0, vx) : Math.max(0, -vx);
    var reflected = intoWall * bounce;
    nextVx = outward * Math.max(reflected, 0.55);
    if (vy > 0.25 && intoWall < 1.2) {
      nextVx += outward * vy * glance;
      nextVy = vy * (1 - glance * 0.35);
    }
  }

  if (hitFloor) {
    nextVy = -Math.max(Math.abs(vy), 0.4) * bounce;
  }

  return { x: nextVx, y: nextVy };
}

function markFlaskBodyEntered(meta, gateY) {
  if (!meta || meta._flaskEntered) return;
  if (!meta.body || !meta.body.position) return;
  var ext = getFlaskBodyVerticalExtents(meta);
  var y = meta.body.position.y;
  if (ext.bodyTop > gateY + 3 && ext.bodyBottom > gateY + 6) meta._flaskEntered = true;
  else if (y > gateY + ext.halfH + 2) meta._flaskEntered = true;
}

function getWallContainmentMinCenterY(geom, radius, meta) {
  var minCenterY = geom.T + radius;
  if (!meta || !isFlaskCargo(meta)) return minCenterY;
  var physicsTopY = getPhysicsMouthGateTopY(geom);
  if (physicsTopY < geom.T) return physicsTopY + radius;
  return minCenterY;
}

function isNearFlaskMouthX(system, x, y, radius, geom) {
  if (!geom) return true;
  var sampleY = Math.max(y, geom.T);
  var half = getErlenmeyerHalfWidthAtY(geom, sampleY);
  return x >= geom.cx - half - radius && x <= geom.cx + half + radius;
}

/** Rim clamp for mouth enforcement — uses raised physics gate when it sits above the visual rim. */
export function getMouthClampTopY(system) {
  var geom = system && system._flaskGeometry;
  if (geom && geom.T != null) {
    var physicsTopY = getPhysicsMouthGateTopY(geom);
    if (physicsTopY < geom.T) return physicsTopY;
  }
  var gateY = getMouthGateTopY(system);
  if (!geom || geom.T == null) return gateY;
  return Math.max(gateY, geom.T);
}

function pushBodyBelowMouthGate(meta, system, vx, vy) {
  var body = meta && meta.body;
  if (!body) return;
  var clampTopY = getMouthClampTopY(system);
  var ext = getFlaskBodyVerticalExtents(meta);
  var enforceTopY = getMouthEnforceTopY(clampTopY, ext.halfH, system.bounds);
  var Body = Matter.Body;
  Body.setPosition(body, { x: body.position.x, y: enforceTopY + ext.halfH + 1 });
  Body.setVelocity(body, {
    x: vx * 0.5,
    y: Math.max(0.8, Math.abs(vy) * 0.15),
  });
}

function dampUpwardVelocityNearGate(system, gateY) {
  if (!system.bodies || system.bodies.length === 0) return;
  var maxUp = getMouthMaxUpwardSpeed(system);
  var clampTopY = getMouthClampTopY(system);
  var geom = system && system._flaskGeometry;
  var Body = Matter.Body;

  for (var i = 0; i < system.bodies.length; i++) {
    var meta = system.bodies[i];
    var body = meta && meta.body;
    if (!body || body.isStatic || !isFlaskCargo(meta)) continue;

    var bodyTop = getFlaskBodyTopY(meta);
    var vx = body.velocity ? body.velocity.x : 0;
    var vy = body.velocity ? body.velocity.y : 0;
    if (vy >= -0.1) continue;

    if (isInExtendedHeadroom(bodyTop, geom)) {
      var headroomCap = maxUp * 2.2;
      if (vy < -headroomCap) {
        Body.setVelocity(body, { x: vx * 0.95, y: -headroomCap });
      }
      continue;
    }

    if (!isAtFlaskRimEdge(bodyTop, clampTopY, system)) continue;

    var nextVy = vy;
    if (vy < -maxUp) nextVy = -maxUp;
    else if (vy < 0) nextVy = vy * 0.35;

    if (nextVy !== vy || Math.abs(vx) > 10) {
      Body.setVelocity(body, { x: vx * 0.88, y: nextVy });
    }
  }
}

/** Force full-rate containment after gameplay disturbances (shake, drop, merge, removal). */
export function requestContainmentBurst(system, durationMs) {
  if (!system) return;
  var rc = system.runConfig || {};
  var fallbackMs = (C.FLASK_CONTAINMENT_BURST_MS != null)
    ? Number(C.FLASK_CONTAINMENT_BURST_MS)
    : 700;
  var ms = (durationMs != null)
    ? Number(durationMs)
    : ((rc.FLASK_CONTAINMENT_BURST_MS != null)
      ? Number(rc.FLASK_CONTAINMENT_BURST_MS)
      : fallbackMs);
  if (!isFinite(ms) || ms <= 0) return;
  var until = Date.now() + ms;
  if (!system._containmentBurstUntil || until > system._containmentBurstUntil) {
    system._containmentBurstUntil = until;
  }
}

export function flaskHasActiveMotion(system, speedThreshold) {
  if (!system.bodies || system.bodies.length === 0) return false;
  var rc = system.runConfig || {};
  var fallbackSpeed = (C.FLASK_CONTAINMENT_ACTIVE_SPEED != null)
    ? Number(C.FLASK_CONTAINMENT_ACTIVE_SPEED)
    : 0.35;
  var threshold = (speedThreshold != null)
    ? Number(speedThreshold)
    : ((rc.FLASK_CONTAINMENT_ACTIVE_SPEED != null)
      ? Number(rc.FLASK_CONTAINMENT_ACTIVE_SPEED)
      : fallbackSpeed);
  threshold = scaleFlaskPhysicsPx(threshold, system.bounds);
  for (var i = 0; i < system.bodies.length; i++) {
    var body = system.bodies[i] && system.bodies[i].body;
    if (!body || body.isStatic) continue;
    if (flaskBodySpeed(body) > threshold) return true;
    var angVel = typeof body.angularVelocity === "number" ? Math.abs(body.angularVelocity) : 0;
    if (angVel > 0.05) return true;
  }
  return false;
}

export function shouldRunContainmentThisFrame(system) {
  if (!system) return false;
  var now = Date.now();
  if (system._containmentBurstUntil && now < system._containmentBurstUntil) return true;
  if (flaskHasActiveMotion(system)) return true;
  var rc = system.runConfig || {};
  var interval = (rc.FLASK_CONTAINMENT_CHECK_INTERVAL != null)
    ? Math.max(1, Math.floor(Number(rc.FLASK_CONTAINMENT_CHECK_INTERVAL)))
    : ((C.FLASK_CONTAINMENT_CHECK_INTERVAL != null)
      ? Math.max(1, Math.floor(Number(C.FLASK_CONTAINMENT_CHECK_INTERVAL)))
      : 5);
  system._containmentTick = (system._containmentTick || 0) + 1;
  return system._containmentTick % interval === 0;
}

export function enforceContainmentIfDue(system) {
  if (!shouldRunContainmentThisFrame(system)) return;
  enforceWallContainment(system);
  if (!system.mouthSealed) {
    enforceMouthContainment(system);
  }
}

/** Mouth + rim guard — run before and after every physics substep while the mouth is sealed. */
export function enforceMouthEscapeContainment(system, phase) {
  if (!system || !system.mouthSealed) return;
  if (phase === "pre") {
    dampUpwardVelocityNearGate(system, getMouthGateTopY(system));
    return;
  }
  enforceMouthContainment(system);
  enforceFlaskTopGuard(system);
}

export function enforceFlaskTopGuard(system) {
  if (!system.bodies || system.bodies.length === 0) return;
  var clampTopY = getMouthClampTopY(system);

  for (var i = 0; i < system.bodies.length; i++) {
    var meta = system.bodies[i];
    var body = meta && meta.body;
    if (!body || body.isStatic || !isFlaskCargo(meta)) continue;

    markFlaskBodyEntered(meta, getMouthGateTopY(system));
    meta._passingMouthGate = false;

    var bodyTop = getFlaskBodyTopY(meta);
    var ext = getFlaskBodyVerticalExtents(meta);
    var enforceTopY = getMouthEnforceTopY(clampTopY, ext.halfH, system.bounds);
    var vx = body.velocity ? body.velocity.x : 0;
    var vy = body.velocity ? body.velocity.y : 0;
    if (bodyTop < enforceTopY) {
      pushBodyBelowMouthGate(meta, system, vx, vy);
    }
  }
}

export function getMouthGateTopY(system) {
  // Primary gate line = lowest slab (largest Y); extra slabs stack above for tunneling backup.
  var gateY = -Infinity;
  if (system.mouthSealBodies && system.mouthSealBodies.length) {
    for (var i = 0; i < system.mouthSealBodies.length; i++) {
      var seal = system.mouthSealBodies[i];
      if (seal && seal.bounds && isFinite(seal.bounds.min.y) && seal.bounds.min.y > gateY) {
        gateY = seal.bounds.min.y;
      }
    }
  }
  if (system.mouthSealBody && system.mouthSealBody.bounds && isFinite(system.mouthSealBody.bounds.min.y)) {
    gateY = Math.max(gateY, system.mouthSealBody.bounds.min.y);
  }
  return isFinite(gateY) ? gateY : 0;
}

// One-way gate at flask mouth (only for external entry while mouth is sealed).
export function shouldIgnoreMouthGateCollision(system, pair) {
  if (!system.mouthSealed || !system.mouthSealBody || !pair) return false;
  var other = getOtherBodyInPair(pair, system.mouthSealBody);
  if (!other) return false;
  if (!system.bodiesMap || !system.bodiesMap[other.id]) return false;
  var meta = system.bodiesMap[other.id];
  var gateY = getMouthGateTopY(system);
  var bodyY = other.position ? other.position.y : gateY;
  var bodyHalfH = getBodyHalfHeight(other);
  var radius = Math.max(meta.radiusX || 0, meta.radiusY || 0, bodyHalfH, 4);
  var bodyTop = bodyY - radius;
  if (other.bounds) bodyTop = Math.min(bodyTop, other.bounds.min.y);
  var vy = other.velocity ? other.velocity.y : 0;

  markFlaskBodyEntered(meta, gateY);

  if (isFlaskCargo(meta)) {
    meta._passingMouthGate = false;
    return false;
  }

  if (meta && meta._passingMouthGate) {
    if (bodyTop > gateY + 2) {
      meta._passingMouthGate = false;
    } else {
      return true;
    }
  }

  if (bodyTop <= gateY + 1 && vy >= -0.05) {
    meta._passingMouthGate = true;
    return true;
  }
  return false;
}

export function enforceMouthContainment(system) {
  if (!system.mouthSealed || !system.bodies || system.bodies.length === 0) return;
  var gateY = getMouthGateTopY(system);
  var clampTopY = getMouthClampTopY(system);
  var geom = system._flaskGeometry;

  for (var i = 0; i < system.bodies.length; i++) {
    var meta = system.bodies[i];
    var body = meta && meta.body;
    if (!body || body.isStatic) continue;

    markFlaskBodyEntered(meta, gateY);
    if (isFlaskCargo(meta)) meta._passingMouthGate = false;

    var ext = getFlaskBodyVerticalExtents(meta);
    var radius = Math.max(ext.halfH, meta.radiusX || 0, meta.radiusY || 0, 4);
    var x = body.position.x;
    var y = body.position.y;
    var vx = body.velocity ? body.velocity.x : 0;
    var vy = body.velocity ? body.velocity.y : 0;
    var prevTop = meta._prevTop != null ? meta._prevTop : getFlaskBodyTopY(meta);
    var bodyTop = getFlaskBodyTopY(meta);
    var enforceTopY = getMouthEnforceTopY(clampTopY, ext.halfH, system.bounds);
    var atRim = isAtFlaskRimEdge(bodyTop, clampTopY, system);
    var sweptTop = bodyTop;
    if (atRim && vy < 0) {
      sweptTop = bodyTop + vy * getMouthSweepFactor();
    }
    var crossedUpward = prevTop > enforceTopY + 0.5 && bodyTop < enforceTopY && vy < 0;
    var breach = bodyTop < enforceTopY
      || (atRim && sweptTop < enforceTopY + 0.5)
      || crossedUpward;
    var nearMouth = isNearFlaskMouthX(system, x, y, radius, geom);
    var enforce = isFlaskCargo(meta) ? breach : (!meta._passingMouthGate && nearMouth && breach);

    if (enforce) {
      pushBodyBelowMouthGate(meta, system, vx, vy);
    }
    meta._prevTop = getFlaskBodyTopY(meta);
    meta._prevY = body.position.y;
  }
}

export function enforceWallContainment(system) {
  if (!system.bodies || system.bodies.length === 0 || !system._flaskGeometry) return;
  var geom = system._flaskGeometry;
  var Body = Matter.Body;
  var cx = geom.cx;

  for (var i = 0; i < system.bodies.length; i++) {
    var meta = system.bodies[i];
    var body = meta && meta.body;
    if (!body || body.isStatic) continue;

    var radius = Math.max(meta.radiusX || 0, meta.radiusY || 0, 4);
    var x = body.position.x;
    var y = body.position.y;
    var vx = body.velocity ? body.velocity.x : 0;
    var vy = body.velocity ? body.velocity.y : 0;
    var cargo = isFlaskCargo(meta);

    if (!cargo && y < geom.T) continue;

    if (!system.mouthSealed) {
      var minY = getWallContainmentMinCenterY(geom, radius, meta);
      if (y < minY) {
        Body.setPosition(body, { x: x, y: minY });
        Body.setVelocity(body, { x: vx * 0.5, y: Math.abs(vy) * 0.3 });
        continue;
      }
    }

    var maxY = geom.bodyBottom - radius;
    var clampedY = y > maxY ? maxY : y;

    var half = getErlenmeyerHalfWidthAtY(geom, clampedY);
    var innerL = geom.L + geom.W;
    var innerR = geom.R - geom.W;
    var minX = Math.max(cx - half + radius, innerL + radius);
    var maxX = Math.min(cx + half - radius, innerR - radius);
    if (maxX < minX) {
      var mid = (maxX + minX) / 2;
      minX = mid;
      maxX = mid;
    }
    var clampedX = clamp(x, minX, maxX);
    var corrected = Math.abs(clampedX - x) > 1e-4 || Math.abs(clampedY - y) > 1e-4;
    if (!corrected) continue;

    Body.setPosition(body, { x: clampedX, y: clampedY });
    var hitRight = x > maxX + 1e-4;
    var hitLeft = x < minX - 1e-4;
    var hitFloor = y > maxY + 1e-4;
    var bounced = applyWallContainmentBounce(vx, vy, hitRight, hitLeft, hitFloor);
    Body.setVelocity(body, bounced);
  }
}

export function resolvePhysicsSubsteps(system, clampedDelta, fixedStepMs, maxSubsteps) {
  var burstMax = (C.FLASK_PHYSICS_BURST_MAX_SUBSTEPS != null)
    ? Number(C.FLASK_PHYSICS_BURST_MAX_SUBSTEPS)
    : 14;
  if (!isFinite(burstMax) || burstMax < maxSubsteps) burstMax = Math.max(maxSubsteps, 12);
  var now = Date.now();
  var bursting = system._containmentBurstUntil && now < system._containmentBurstUntil;
  var burstSpeed = (C.FLASK_CONTAINMENT_BURST_SPEED != null)
    ? Number(C.FLASK_CONTAINMENT_BURST_SPEED)
    : 1.2;
  if (!isFinite(burstSpeed) || burstSpeed <= 0) burstSpeed = 1.2;
  var active = flaskHasActiveMotion(system, burstSpeed);
  var cap = (bursting || active) ? Math.max(maxSubsteps, burstMax) : maxSubsteps;
  return Math.max(1, Math.min(cap, Math.ceil(clampedDelta / fixedStepMs)));
}